import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import CardMembershipIcon from '@mui/icons-material/CardMembership'
import { certificateApi } from '../utils/api'

const AWSCertificateTable = () => {
  const [certificates, setCertificates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  useEffect(() => {
    fetchCertificates()
  }, [paginationModel])

  const fetchCertificates = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await certificateApi.getAll({
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
      })
      
      // Show both on-prem and AWS certificates
      let filteredCerts = response.data.items.map(cert => ({
        id: cert.cert_id,
        commonName: cert.common_name,
        sanEntries: cert.subject_alt_names
          ? cert.subject_alt_names.split(',').map(s => s.trim()).filter(Boolean).join(', ')
          : '—',
        accountName: cert.aws_account === 'ONPREM' 
          ? 'On-Premises' 
          : (cert.account_name || `aws-${cert.product_name}-account`),
        accountNumber: cert.aws_account,
        renewalStatus: cert.renewal_status || 'N/A',
        renewalCRQ: cert.renewal_crq || '',
        layer7CRQ: cert.layer7_crq || '',
        acmUsage: cert.acm_usage ? 'Yes' : 'No',
        productName: cert.product_name || cert.product_code,
        expiryDate: cert.expiration_date ? new Date(cert.expiration_date).toLocaleDateString() : 'N/A',
        environment: cert.sdlc || 'N/A',
        certType: cert.cert_type || 'N/A',
        layer7: cert.layer7 ? 'Yes' : 'No',
        clientInvolvement: cert.client_involvement ? 'Yes' : 'No',
        dependencyPresent: cert.dependency_present ? 'Yes' : 'No',
        updateRequired: cert.requires_update ? 'Yes' : 'No',
        status: formatStatus(cert.status),
        certificateType: cert.aws_account === 'ONPREM' ? 'On-Prem' : 'AWS',
      }))

      setCertificates(filteredCerts)
      setRowCount(filteredCerts.length)
    } catch (err) {
      console.error('Error fetching certificates:', err)
      setError(err.response?.data?.detail || 'Failed to load certificates. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const deriveEnvironment = (accountNum) => {
    // This is a simple heuristic - adjust based on your actual logic
    const lastDigit = parseInt(accountNum.slice(-1))
    if (lastDigit <= 2) return 'dev'
    if (lastDigit <= 5) return 'qa'
    if (lastDigit <= 7) return 'uat'
    return 'prd'
  }

  const formatStatus = (status) => {
    const statusMap = {
      'ACTIVE': 'Active',
      'EXPIRING_SOON': 'Expiring Soon',
      'EXPIRED': 'Expired',
      'PENDING': 'Pending',
      'ONBOARDED': 'Onboarded',
      'REVOKED': 'Revoked',
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
      case 'Onboarded':
        return 'success'
      case 'Expiring Soon':
        return 'warning'
      case 'Expired':
      case 'Revoked':
        return 'error'
      case 'Pending':
        return 'info'
      default:
        return 'default'
    }
  }

  const renderCellWithWrap = (params) => {
    const value = params.value || ''
    const shouldWrap = value.length > 40
    
    return (
      <Box
        sx={{
          whiteSpace: shouldWrap ? 'normal' : 'nowrap',
          wordWrap: shouldWrap ? 'break-word' : 'normal',
          lineHeight: '1.5',
          padding: '8px 0',
        }}
      >
        {value}
      </Box>
    )
  }

  const renderProductCell = (params) => {
    const value = params.value || ''
    const shouldWrap = value.length > 50
    
    return (
      <Box
        sx={{
          whiteSpace: shouldWrap ? 'normal' : 'nowrap',
          wordWrap: shouldWrap ? 'break-word' : 'normal',
          wordBreak: shouldWrap ? 'break-word' : 'normal',
          lineHeight: '1.5',
          padding: '8px 0',
          width: '100%',
          overflow: 'hidden',
          textOverflow: shouldWrap ? 'clip' : 'ellipsis',
        }}
      >
        {value}
      </Box>
    )
  }

  const columns = [
    {
      field: 'certificateType',
      headerName: 'Type',
      minWidth: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value === 'AWS' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'commonName',
      headerName: 'Common Name',
      minWidth: 250,
      flex: 1,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'sanEntries',
      headerName: 'SAN Entries',
      minWidth: 220,
      flex: 1,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'productName',
      headerName: 'Product',
      width: 200,
      renderCell: renderProductCell,
    },
    {
      field: 'accountName',
      headerName: 'AWS Account',
      minWidth: 200,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'accountNumber',
      headerName: 'Account #',
      minWidth: 130,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'renewalStatus',
      headerName: 'Renewal',
      minWidth: 100,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'renewalCRQ',
      headerName: 'Renewal CRQ',
      minWidth: 120,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'layer7CRQ',
      headerName: 'Layer7 CRQ',
      minWidth: 120,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'acmUsage',
      headerName: 'ACM',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'expiryDate',
      headerName: 'Expiry Date',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'environment',
      headerName: 'SDLC',
      minWidth: 90,
      renderCell: (params) => (
        <Chip 
          label={params.value.toUpperCase()} 
          size="small" 
          color={params.value === 'prd' ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'certType',
      headerName: 'Cert Type',
      minWidth: 100,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'layer7',
      headerName: 'Layer7',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'clientInvolvement',
      headerName: 'Client',
      minWidth: 80,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'dependencyPresent',
      headerName: 'Dependency',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
    {
      field: 'updateRequired',
      headerName: 'Update Req.',
      minWidth: 110,
      renderCell: renderCellWithWrap,
    },
  ]

  const handleExport = () => {
    // Add export functionality
    console.log('Exporting certificates...')
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CardMembershipIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Certificates
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchCertificates} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export">
            <IconButton onClick={handleExport} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={certificates}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={rowCount}
            paginationMode="server"
            checkboxSelection
            disableSelectionOnClick
            loading={loading}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                paddingTop: '8px',
                paddingBottom: '8px',
              },
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
                fontWeight: 'bold',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  )
}

export default AWSCertificateTable
